<script setup>
import { ref, onMounted } from 'vue'
import { useContentStore } from '../store/content'

const props = defineProps({
  sections: {
    type: Array,
    required: true
  },
  title: {
    type: String,
    required: true
  }
})

const contentStore = useContentStore()
const activeSection = ref(null)
const sectionContent = ref('')
const sectionDocuments = ref([])
const loading = ref(false)

const loadSectionContent = async (section) => {
  if (!section) return
  
  activeSection.value = section
  loading.value = true
  
  try {
    // Load section content
    const content = await contentStore.getSectionContent(section.id)
    sectionContent.value = content?.content || ''
    
    // Load section documents
    const docs = await contentStore.getSectionDocuments(section.id)
    sectionDocuments.value = docs || []
    
  } catch (error) {
    console.error('Error loading section content:', error)
  } finally {
    loading.value = false
  }
}

// Load first section by default
onMounted(() => {
  if (props.sections && props.sections.length > 0) {
    loadSectionContent(props.sections[0])
  }
})
</script>

<template>
  <main class="section-viewer">
    <header class="viewer-header">
      <router-link to="/portal-transparencia" class="back-link">
        <i class="pi pi-arrow-left" aria-hidden="true"></i>
        Portal de Transparencia
      </router-link>
      <p class="viewer-eyebrow">Consulta pública</p>
      <h1>{{ title }}</h1>
    </header>

    <div class="viewer-layout">
      <aside class="viewer-nav" aria-label="Secciones disponibles">
        <div class="nav-title">
          <span>Secciones</span>
          <strong>{{ sections.length }}</strong>
        </div>

        <div class="section-list">
          <button
            v-for="(section, index) in sections"
            :key="section.id"
            type="button"
            class="section-item"
            :class="{ selected: activeSection?.id === section.id }"
            @click="loadSectionContent(section)"
          >
            <span class="section-number">{{ index + 1 }}</span>
            <span>{{ section.title }}</span>
          </button>
        </div>
      </aside>

      <section class="viewer-content">
        <div v-if="activeSection" class="content-panel">
          <div class="panel-header">
            <div>
              <p>Sección seleccionada</p>
              <h2>{{ activeSection.title }}</h2>
            </div>
            <span class="document-count">
              {{ sectionDocuments.length }} documento{{ sectionDocuments.length === 1 ? '' : 's' }}
            </span>
          </div>

          <div v-if="loading" class="loading-state">
            <ProgressSpinner />
          </div>

          <div v-else class="panel-body">
            <div class="section-content" v-if="sectionContent">
              <div v-html="sectionContent"></div>
            </div>

            <div v-if="sectionDocuments.length > 0" class="documents-block">
              <div class="documents-heading">
                <h3>Documentos disponibles</h3>
              </div>

              <a
                v-for="doc in sectionDocuments"
                :key="doc.id"
                :href="doc.url"
                target="_blank"
                rel="noopener noreferrer"
                class="document-card"
              >
                <span class="document-icon">
                  <i class="pi pi-file-pdf" aria-hidden="true"></i>
                </span>
                <span class="document-info">
                  <strong>{{ doc.name }}</strong>
                  <span v-if="doc.comentario">{{ doc.comentario }}</span>
                  <span v-else-if="doc.uploadedAt">Publicado: {{ new Date(doc.uploadedAt).toLocaleDateString('es-CO') }}</span>
                  <span v-else>Documento institucional</span>
                </span>
                <i class="pi pi-download document-download" aria-hidden="true"></i>
              </a>
            </div>

            <div v-else class="empty-documents">
              <span class="empty-icon">
                <i class="pi pi-folder-open" aria-hidden="true"></i>
              </span>
              <h3>No hay documentos publicados</h3>
              <p>Esta sección aún no tiene documentos disponibles para consulta.</p>
            </div>
          </div>
        </div>

        <div v-else class="empty-documents empty-main">
          <span class="empty-icon">
            <i class="pi pi-info-circle" aria-hidden="true"></i>
          </span>
          <h3>No hay secciones disponibles</h3>
          <p>No se encontraron secciones para mostrar.</p>
        </div>
      </section>
    </div>
  </main>
</template>

<style scoped>
.section-viewer {
  min-height: 100vh;
  padding: 36px 24px 64px;
  background:
    linear-gradient(180deg, #f7fbf7 0%, #eef6ef 44%, #ffffff 100%);
  color: #102716;
}

.viewer-header,
.viewer-layout {
  max-width: 1180px;
  margin: 0 auto;
}

.viewer-header {
  padding: 18px 0 26px;
}

.back-link {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 26px;
  color: #19642d;
  font-weight: 800;
  text-decoration: none;
}

.back-link:hover {
  color: #0b3d1d;
}

.viewer-eyebrow {
  margin: 0 0 10px;
  color: #008300;
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.viewer-header h1 {
  max-width: 850px;
  margin: 0;
  color: #102716;
  font-size: clamp(2rem, 5vw, 3.4rem);
  line-height: 1.05;
  letter-spacing: 0;
}

.viewer-layout {
  display: grid;
  grid-template-columns: 330px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.viewer-nav,
.content-panel,
.empty-main {
  border: 1px solid #e3ece5;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 16px 45px rgba(15, 55, 24, 0.08);
}

.viewer-nav {
  position: sticky;
  top: 20px;
  overflow: hidden;
}

.nav-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 18px 14px;
  border-bottom: 1px solid #e7efe9;
}

.nav-title span {
  color: #102716;
  font-weight: 900;
}

.nav-title strong {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  border-radius: 999px;
  color: #008300;
  background: #e8f6ea;
  font-size: 0.9rem;
}

.section-list {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
  padding: 10px;
}

.section-item {
  display: flex;
  width: 100%;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #314b39;
  font-weight: 700;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.section-item:hover,
.section-item:focus-visible {
  background: #f0f7f1;
  color: #0f3f1f;
  outline: none;
}

.section-item.selected {
  color: #ffffff;
  background: #008300;
  box-shadow: 0 8px 20px rgba(0, 131, 0, 0.18);
}

.section-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: rgba(0, 131, 0, 0.1);
  color: #008300;
  font-size: 0.8rem;
  font-weight: 900;
}

.section-item.selected .section-number {
  color: #008300;
  background: #ffffff;
}

.viewer-content {
  min-width: 0;
}

.content-panel {
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
  padding: 26px;
  border-bottom: 1px solid #e7efe9;
  background:
    linear-gradient(135deg, rgba(0, 131, 0, 0.08), rgba(29, 78, 216, 0.06));
}

.panel-header p {
  margin: 0 0 8px;
  color: #008300;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.panel-header h2 {
  margin: 0;
  color: #102716;
  font-size: clamp(1.45rem, 3vw, 2.2rem);
  line-height: 1.18;
  letter-spacing: 0;
}

.document-count {
  flex: 0 0 auto;
  padding: 8px 12px;
  border-radius: 999px;
  color: #19642d;
  background: #ffffff;
  font-size: 0.85rem;
  font-weight: 900;
  box-shadow: 0 8px 20px rgba(15, 55, 24, 0.08);
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 72px 24px;
}

.panel-body {
  padding: 26px;
}

.section-content {
  margin-bottom: 24px;
  color: #304b39;
  line-height: 1.65;
}

.documents-block {
  display: grid;
  gap: 12px;
}

.documents-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.documents-heading h3 {
  margin: 0;
  color: #102716;
  font-size: 1.15rem;
  letter-spacing: 0;
}

.document-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  padding: 14px;
  border: 1px solid #e3ece5;
  border-radius: 8px;
  color: inherit;
  background: #ffffff;
  text-decoration: none;
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.document-card:hover,
.document-card:focus-visible {
  transform: translateY(-2px);
  border-color: #9ccfa8;
  box-shadow: 0 12px 26px rgba(15, 55, 24, 0.1);
  outline: none;
}

.document-icon,
.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.document-icon {
  width: 44px;
  height: 44px;
  color: #b42318;
  background: #fff1ef;
  font-size: 1.15rem;
}

.document-info {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.document-info strong {
  overflow: hidden;
  color: #102716;
  font-size: 0.98rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-info span {
  overflow: hidden;
  color: #637768;
  font-size: 0.86rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-download {
  color: #008300;
}

.empty-documents {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  padding: 36px 24px;
  border: 1px dashed #bfd7c5;
  border-radius: 8px;
  background: #f9fcfa;
  text-align: center;
}

.empty-main {
  border-style: solid;
}

.empty-icon {
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
  color: #008300;
  background: #e8f6ea;
  font-size: 1.35rem;
}

.empty-documents h3 {
  margin: 0 0 8px;
  color: #102716;
  font-size: 1.2rem;
  letter-spacing: 0;
}

.empty-documents p {
  max-width: 430px;
  margin: 0;
  color: #637768;
  line-height: 1.5;
}

@media (max-width: 980px) {
  .viewer-layout {
    grid-template-columns: 1fr;
  }

  .viewer-nav {
    position: static;
  }

  .section-list {
    max-height: none;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 620px) {
  .section-viewer {
    padding: 24px 14px 48px;
  }

  .section-list {
    grid-template-columns: 1fr;
  }

  .panel-header {
    flex-direction: column;
    padding: 22px 18px;
  }

  .panel-body {
    padding: 18px;
  }

  .document-card {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .document-download {
    display: none;
  }
}
</style>
